import {
  GroupByMode,
  TimelineScrollAnchor,
  TimelineScrollRequest,
  TimelineTimeframes,
} from '@griply/types';
import { buildTimelinePreviewData, getWeekStartsOn } from '@griply/utils';
import { startOfYear } from 'date-fns';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { PremiumOverlay } from 'shared/components/connected/premium-overlay';
import { GoalTimelineContainer } from 'shared/components/ui/timeline';
import { TaskCountsContext } from 'shared/contexts/task-counts/context';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';

import * as Styled from './timeline-premium-lock.style';

export type TimelinePremiumLockProps = {
  title: string;
};

// The preview goals span Jan–Dec, so the year start stays at the left edge instead of centering today.
const timeframeStartScroll: TimelineScrollRequest = {
  anchor: TimelineScrollAnchor.TimeframeStart,
  id: 0,
};

export const TimelinePremiumLock: React.FC<TimelinePremiumLockProps> = ({
  title,
}) => {
  const { t } = useTranslation();
  const today = useToday();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);

  const { goals, lifeAreas, goalProgressCounts } = useMemo(
    () => buildTimelinePreviewData(today),
    [today],
  );

  const yearStart = useMemo(() => startOfYear(today), [today]);

  // GoalBar reads progress from this context, not props — real counts render every preview ring at 0%.
  const previewCounts = useMemo(
    () => ({
      counts: {
        today: 0,
        inbox: 0,
        bySmartListId: new Map(),
        byTagId: new Map(),
        byGoalId: new Map(),
        byGoalProgress: goalProgressCounts,
      },
    }),
    [goalProgressCounts],
  );

  return (
    <Styled.Container>
      <PremiumOverlay
        title={t('goals.timeline.premium.title')}
        description={t('goals.timeline.premium.description')}
      >
        <Styled.TimelineWrapper>
          <TaskCountsContext.Provider value={previewCounts}>
            <GoalTimelineContainer
              timeframe={TimelineTimeframes.Year}
              referenceDate={yearStart}
              scrollRequest={timeframeStartScroll}
              goals={goals}
              allGoals={goals}
              lifeAreas={lifeAreas}
              weekStartsOn={weekStartsOn}
              groupBy={GroupByMode.LifeArea}
              title={title}
            />
          </TaskCountsContext.Provider>
        </Styled.TimelineWrapper>
      </PremiumOverlay>
    </Styled.Container>
  );
};
